import { useCallback, useContext, useState } from "react";
import AuthContext from "@/contexts/AuthContext";
import { PlayersService } from "@/core/http/services/PlayersService";
import { RankingService } from "@/core/http/services/RankingService";
import { useParams } from "react-router-dom";

const normalizeMatch = (match) => ({
  id: match?.id,
  finished: Boolean(match?.finished),
  teamAPlayerIds: (match?.teamA?.players || []).map((player) => player?.id),
  teamBPlayerIds: (match?.teamB?.players || []).map((player) => player?.id),
  teamA: (match?.teamA?.players || []).map(
    (player) => player?.name || "Jogador",
  ),
  teamB: (match?.teamB?.players || []).map(
    (player) => player?.name || "Jogador",
  ),
  score:
    match?.teamA?.score !== null &&
    match?.teamA?.score !== undefined &&
    match?.teamB?.score !== null &&
    match?.teamB?.score !== undefined
      ? {
          teamA: match.teamA.score,
          teamB: match.teamB.score,
        }
      : null,
});

const getRoundTitle = (round) => {
  if (round?.status === "ACTIVE")
    return `Rodada ${round?.number || "N/A"} em andamento`;
  if (round?.status === "FINISHED")
    return `Rodada ${round?.number || "N/A"} finalizada`;
  return `Rodada ${round?.number || "N/A"}`;
};

const normalizeRound = (roundItem, index) => ({
  id: roundItem?.round?.id ?? `round-${index}`,
  title: getRoundTitle(roundItem?.round),
  status: roundItem?.round?.status ?? null,
  createdAt: roundItem?.round?.createdAt ?? null,
  matches: (roundItem?.matches || []).map(normalizeMatch),
});

const normalizeRanking = (ranking) => ({
  id: ranking?.id ?? null,
  name: ranking?.name ?? "Ranking",
  year: ranking?.year ?? null,
});

const useMatchesUser = () => {
  const { userLoggedData } = useContext(AuthContext);
  const playerId = userLoggedData?.playerId;
  const { clubId: clubIdFromUrl } = useParams();
  const clubId = clubIdFromUrl || userLoggedData?.selectedClubId || null;

  const [loading, setLoading] = useState({
    rankings: false,
    matches: false,
  });
  const [error, setError] = useState({ type: null, message: null });
  const [rankingsData, setRankingsData] = useState([]);
  const [rankingMatchesData, setRankingMatchesData] = useState([]);

  const getRankings = useCallback(async () => {
    if (!playerId) {
      setRankingsData([]);
      setRankingMatchesData([]);
      return [];
    }

    setLoading((prev) => ({ ...prev, rankings: true }));
    setError({ type: null, message: null });

    try {
      const { status, data, message } =
        await PlayersService.getRankings(playerId);

      if (data?.error || status !== 200) {
        setError({
          type: "rankings",
          message: message || "Erro ao buscar os rankings do jogador.",
        });
        setRankingsData([]);
        return [];
      }

      const normalizedRankings = (data || []).map(normalizeRanking);
      setRankingsData(normalizedRankings);
      return normalizedRankings;
    } catch (err) {
      setError({
        type: "rankings",
        message:
          err?.message || "Erro inesperado ao buscar os rankings do jogador.",
      });
      setRankingsData([]);
      return [];
    } finally {
      setLoading((prev) => ({ ...prev, rankings: false }));
    }
  }, [playerId]);

  const getRankingMatches = useCallback(
    async (rankings = []) => {
      if (!clubId || !playerId || rankings.length === 0) {
        setRankingMatchesData([]);
        return;
      }

      setLoading((prev) => ({ ...prev, matches: true }));

      try {
        const responses = await Promise.all(
          rankings.map((ranking) =>
            RankingService.getRankingMatches({
              clubId,
              playerId,
              rankingId: ranking.id,
            }),
          ),
        );

        const failedResponse = responses.find(
          (response) => response.status !== 200 || response.data?.error,
        );

        if (failedResponse) {
          setError({
            type: "matches",
            message:
              failedResponse.message ||
              "Erro ao buscar as partidas dos rankings.",
          });
          setRankingMatchesData([]);
          return;
        }
        console.log("responses", responses);

        const normalizedMatches = responses.map((response, index) => ({
          ...rankings[index],
          title: rankings[index]?.year
            ? `${rankings[index].name} • ${rankings[index].year}`
            : rankings[index]?.name,
          rounds: (response.data?.rounds || []).map(normalizeRound).reverse(),
        }));

        console.log("normalizedMatches", normalizedMatches);

        setRankingMatchesData(normalizedMatches);
      } catch (err) {
        setError({
          type: "matches",
          message:
            err?.message ||
            "Erro inesperado ao buscar as partidas dos rankings.",
        });
        setRankingMatchesData([]);
      } finally {
        setLoading((prev) => ({ ...prev, matches: false }));
      }
    },
    [clubId, playerId],
  );

  const initialize = useCallback(async () => {
    const rankings = await getRankings();
    await getRankingMatches(rankings);
  }, [getRankings, getRankingMatches]);

  const hasMultipleRankings = rankingMatchesData.length > 1;
  const isWithoutSeason =
    !loading.rankings && !loading.matches && rankingsData.length === 0;

  return {
    playerId,
    rankingsData,
    rankingMatchesData,
    hasMultipleRankings,
    isWithoutSeason,
    loading,
    error,
    initialize,
  };
};

export default useMatchesUser;
